import React, { useState } from "react";
import ConstructionIcon from "@mui/icons-material/Construction";
import BusinessNameSearchBar from "./BusinessNameSearchBar";
import SearchNew from "./SearchNew";
import SearchCategoryTable from "./SearchCategoryTable";

const VENDOR_TYPE_IMAGE_FILTER =
  "grayscale(1) brightness(0.58) contrast(0.92)";

const VENDOR_TYPE_OPTIONS = [
  {
    key: "all",
    label: "Both",
    searchValue: "",
    facetValues: [],
    filterStr: "",
    Icon: ConstructionIcon,
  },
  {
    key: "contractor",
    label: "Contractor",
    searchValue: "Contractor (Providing and Fixing)",
    facetValues: ["Contractor (Providing and Fixing)", "Labour Contractor"],
    filterStr:
      'businessProfile.businessCategory_str:("Contractor (Providing and Fixing)" OR "Labour Contractor")',
    iconSrc:
      "https://storagereponeevaydevcdn.blob.core.windows.net/business/user_icon_searchbar.svg",
    iconAlt: "Contractor",
  },
  {
    key: "material",
    label: "Material",
    searchValue: "Material Supplier",
    facetValues: ["Material Supplier"],
    filterStr: 'businessProfile.businessCategory_str:("Material Supplier")',
    Icon: ConstructionIcon,
  },
];

const NeevayNetworkHeader = ({
  selectedVendorTypeKey = '',
  onVendorTypeChange,
  showCategories,
  showNoResults,
  hideSearchSection = false,
  showSearchByCategory,
  showResultsToolbar = false,
  showFilters = false,
  onToggleFilters,
  onBackToMainSearch,
  onResultsToolbarSummaryMount,
  onResultsToolbarActionsMount,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const selectedVendorType = VENDOR_TYPE_OPTIONS.find((option) => option.key === (selectedVendorTypeKey || "all")) || VENDOR_TYPE_OPTIONS[0];

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      <div className="sticky top-0 z-40 px-3 pt-3 lg:px-4">
        {hideSearchSection && typeof onBackToMainSearch === "function" && (
          <div className="mb-1 flex justify-start">
            <button
              type="button"
              onClick={onBackToMainSearch}
              className="text-xs font-semibold text-blue-600 underline underline-offset-4 transition-colors hover:text-blue-800"
            >
              Back to previous selections
            </button>
          </div>
        )}
        {!hideSearchSection && (
        <div className="relative overflow-visible rounded-md border border-[#F2DDD1] bg-white px-3 py-2.5 shadow-[0_16px_40px_rgba(180,58,18,0.08)]">
          <div className="grid min-w-0 grid-cols-1 gap-3 xl:grid-cols-[minmax(230px,1fr)_minmax(250px,300px)_minmax(400px,2fr)] xl:items-end">
            <div className="min-w-0">
              <p className="mb-1.5 text-sm font-bold text-[#111827]">
                Business name
              </p>
              <BusinessNameSearchBar vendorType={selectedVendorType?.key || ""} />
            </div>

            <div className="min-w-0">
              <p className="mb-1.5 text-sm font-bold text-[#111827]">
                Vendor type
              </p>
              <div className="grid h-[42px] grid-cols-3 overflow-hidden rounded-md border border-[#F1D7C8] bg-white/95 shadow-sm">
                {VENDOR_TYPE_OPTIONS.map((option, index) => {
                  const isSelected = selectedVendorType?.key === option.key;
                  const Icon = option.Icon;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => onVendorTypeChange?.(option.key)}
                      className={`inline-flex min-w-0 items-center justify-center gap-2 px-2 text-xs font-medium transition ${
                        isSelected
                          ? "bg-[linear-gradient(135deg,#FFF1E8_0%,#FFF9F3_100%)] text-[#B43A12] shadow-inner"
                          : "bg-white text-[#374151] hover:bg-[#FFF8F3]"
                      } ${index > 0 ? "border-l border-[#F1D7C8]" : ""}`}
                    >
                      <span
                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                          isSelected ? "bg-white" : "bg-[#FFF4EE]"
                        }`}
                      >
                        {option.iconSrc ? (
                          <img
                            src={option.iconSrc}
                            alt={option.iconAlt || option.label}
                            className="h-3.5 w-3.5 shrink-0 object-contain opacity-95"
                            style={{ filter: VENDOR_TYPE_IMAGE_FILTER }}
                          />
                        ) : (
                          <Icon className="h-3.5 w-3.5 shrink-0 text-[#7A8597]" />
                        )}
                      </span>
                      <span className="truncate">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="min-w-0">
              <p className="mb-1.5 text-sm font-bold text-[#111827]">
                Search by material / service and region
              </p>
              <SearchNew
                compact
                externalVendorType={selectedVendorType?.searchValue || ""}
                externalFilterStr={selectedVendorType?.filterStr || ""}
                externalFacetValue={selectedVendorType?.searchValue || ""}
                externalFacetValues={selectedVendorType?.facetValues || []}
              />
            </div>
          </div>
        </div>
        )}

        {showResultsToolbar && (
          <div className="my-2 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#E8E8E0] bg-white/95 px-3 py-2 shadow-sm backdrop-blur">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">

              {typeof onToggleFilters === "function" && (
                <button
                  type="button"
                  onClick={onToggleFilters}
                  className={`inline-flex shrink-0 items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition ${
                    showFilters
                      ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                      : "border-[#DADAD0] bg-white text-[#303026] hover:border-[#E95420] hover:text-[#B43A12]"
                  }`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 5h18M6 12h12M10 19h4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                  Filters
                </button>
              )}

              <div
                ref={onResultsToolbarSummaryMount}
                className="min-w-0 flex-1"
              />
            </div>

            <div
              className="flex flex-wrap items-center gap-3"
            >
              <div ref={onResultsToolbarActionsMount} className="contents" />


              {showSearchByCategory && (
                <button
                  type="button"
                  onClick={toggleDrawer}
                  className="inline-flex shrink-0 items-center gap-2 rounded-md border border-[#F7B99D] bg-[#FFF4EE] px-3 py-2 text-xs font-medium text-[#B43A12] transition-colors hover:border-[#E95420]"
                >
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[11px] font-bold text-[#E95420] shadow-sm">
                    +
                  </span>
                  Search by Category
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={`fixed inset-0 z-50 bg-black transition-all duration-300 ${
          isDrawerOpen
            ? "pointer-events-auto opacity-30 backdrop-blur-[2px]"
            : "pointer-events-none opacity-0"
        }`}
        onClick={closeDrawer}
      />

      <div
        className={`fixed inset-y-0 right-0 z-[60] flex max-w-full items-stretch px-3 py-3 transition-all duration-300 lg:px-4 ${
          isDrawerOpen
            ? "pointer-events-auto"
            : "pointer-events-none"
        }`}
      >
        <div
          className={`flex h-full w-[min(1100px,calc(100vw-24px))] flex-col overflow-hidden rounded-[28px] border border-[#E8E8E0] bg-white shadow-[0_24px_80px_rgba(31,30,41,0.18)] transition-all duration-300 ease-out lg:w-[min(1120px,calc(100vw-32px))] ${
            isDrawerOpen
              ? "translate-x-0 opacity-100"
              : "translate-x-8 opacity-0"
          }`}
        >
          <div className="flex items-start justify-between border-b border-[#F1DCCF] bg-white/95 px-5 py-4 backdrop-blur">
            <div>
              <p className="text-[15px] font-semibold text-[#14161B]">
                Browse by Category
              </p>
              <p className="mt-0.5 text-sm text-[#6B6E76]">
                Search vendors from the marketplace by activity and city.
              </p>
            </div>
            <button
              type="button"
              onClick={closeDrawer}
              className="rounded-full p-2 text-gray-500 transition-colors hover:bg-[#FFF4EE] hover:text-[#B43A12]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto bg-[linear-gradient(180deg,#FFF8F3_0%,#FCFCFA_20%,#FFFFFF_100%)] p-4 lg:p-5">
            <SearchCategoryTable allowedCategoryType={selectedVendorType?.key || "none"} onClose={closeDrawer} />
          </div>
        </div>
      </div>
    </>
  );
};

export default NeevayNetworkHeader;




